/**
 * Sesión del staff en React (contrato §1, AF §11).
 *
 * Qué resuelve, en el orden en que importa:
 * 1. **Login con 2FA** (RN-ROL-17): `login()` puede devolver un desafío en vez de tokens.
 * 2. **Sede activa** (RN-ROL-08): al entrar, si el usuario tiene más de una sede, hay que
 *    elegirla; el cambio se avisa al backend, que lo audita.
 * 3. **Expiración por inactividad** (RN-ROL-16): 8 h sin actividad cierran la sesión sola,
 *    con un aviso explícito de por qué.
 * 4. Rehidratación: `GET /api/me` al arrancar, para que un usuario desactivado no siga
 *    "adentro" sólo porque su token todavía no venció (RN-ROL-09).
 * 5. **«Ver como rol»** (sólo el Dueño, `preview.ts`): mientras dura, `user.role` es el rol
 *    simulado y `branches` es sólo la sede elegida, así el menú, las guardas y cada
 *    pantalla se dibujan como para ese rol. La sesión frente a la API sigue siendo la real
 *    y las escrituras quedan frenadas en `lib/api.ts`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AuthUser, UserRole } from "@viva/shared";
import { ApiError, http } from "../api.js";
import type { MeBranch, MeResponse } from "../types.js";
import { disconnectRealtime } from "../realtime.js";
import {
  clearSession,
  getActiveBranchId,
  getRefreshToken,
  getSession,
  hydrateSession,
  isIdleExpired,
  setActiveBranchId as persistActiveBranch,
  setSession,
  setUser as persistUser,
  touchActivity,
} from "./session.js";
import { PORTAL_IDLE_HOURS } from "./surface.js";
import { clearRolePreview, getRolePreview, setRolePreview, type RolePreview } from "./preview.js";

export type AuthStatus = "loading" | "anonymous" | "authenticated";

export interface TwoFactorChallenge {
  twoFactorRequired: true;
  challengeId: string;
}

export type LoginResult = { ok: true } | TwoFactorChallenge;

export interface AuthContextValue {
  status: AuthStatus;
  /** El usuario tal como se dibuja: en la vista previa, con el rol simulado. */
  user: AuthUser | null;
  /** El rol REAL de la sesión, con o sin vista previa. Lo usa lo que sólo ve el Dueño. */
  realRole: UserRole | null;
  /** La vista previa de un rol en curso, o `null`. */
  rolePreview: RolePreview | null;
  branches: MeBranch[];
  activeBranchId: string | null;
  activeBranch: MeBranch | null;
  /** RN-ROL-08: hay que elegir sede cuando el usuario tiene más de una y no eligió. */
  needsBranchChoice: boolean;
  /** Motivo por el que se cerró la sesión sola (inactividad, desactivación). */
  signedOutReason: string | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyTwoFactor: (challengeId: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  chooseBranch: (branchId: string) => Promise<void>;
  refreshMe: () => Promise<void>;
  /** Entra a «ver como rol» con esa sede. Sólo el Dueño; los demás reciben un error. */
  startRolePreview: (role: UserRole, branchId: string) => Promise<void>;
  /** Sale de la vista previa y restaura la sede activa que había antes de entrar. */
  stopRolePreview: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Cada cuánto se controla la inactividad. No hace falta más fino que un minuto. */
const IDLE_CHECK_MS = 60_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUserState] = useState<AuthUser | null>(null);
  const [branches, setBranches] = useState<MeBranch[]>([]);
  const [activeBranchId, setActiveBranchIdState] = useState<string | null>(null);
  const [signedOutReason, setSignedOutReason] = useState<string | null>(null);
  const [rolePreview, setRolePreviewState] = useState<RolePreview | null>(() => getRolePreview());
  const hydrated = useRef(false);

  const applyMe = useCallback((me: MeResponse) => {
    setUserState(me.user);
    persistUser(me.user);
    setBranches(me.branches);

    const stored = getActiveBranchId();
    const valid = stored && me.branches.some((branch) => branch.id === stored) ? stored : null;
    // Si no hay sede guardada válida en el navegador, se toma la que sugiere el backend
    // o la primera sede OPERATIVA, para poder operar inmediatamente. `Central` es la sede
    // virtual de los gastos compartidos: sin mostrador, ni stock, ni caja.
    const fallbackDefault =
      me.activeBranchId && me.branches.some((branch) => branch.id === me.activeBranchId)
        ? me.activeBranchId
        : ((me.branches.find((branch) => branch.kind === "operational") ?? me.branches[0])?.id ??
          null);
    const next = valid ?? fallbackDefault;
    setActiveBranchIdState(next);
    persistActiveBranch(next);
  }, []);

  const signOutLocally = useCallback((reason: string | null) => {
    clearRolePreview();
    setRolePreviewState(null);
    clearSession();
    disconnectRealtime();
    setUserState(null);
    setBranches([]);
    setActiveBranchIdState(null);
    setSignedOutReason(reason);
    setStatus("anonymous");
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      applyMe(await http.get<MeResponse>("/api/me"));
      setStatus("authenticated");
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        signOutLocally(
          error.status === 403 ? "Tu usuario está desactivado. Hablá con Administración." : null,
        );
        return;
      }
      // Un backend caído no debería desloguear a nadie: se queda con lo que había.
      setStatus(getSession() ? "authenticated" : "anonymous");
    }
  }, [applyMe, signOutLocally]);

  // Arranque: rehidrata y confirma contra el backend.
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    hydrateSession();
    const session = getSession();
    if (!session) {
      setStatus("anonymous");
      return;
    }
    setUserState(session.user);
    setActiveBranchIdState(getActiveBranchId());
    void refreshMe();
  }, [refreshMe]);

  // La vista previa es del Dueño: si la sesión real es de otro rol (otro usuario entró en
  // esta pestaña), no se arrastra.
  useEffect(() => {
    if (user && user.role !== "owner" && rolePreview) {
      clearRolePreview();
      setRolePreviewState(null);
    }
  }, [user, rolePreview]);

  // RN-ROL-16: expiración por inactividad de 8 h, con aviso de por qué.
  useEffect(() => {
    if (status !== "authenticated") return;

    const onActivity = () => touchActivity();
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "focus"];
    for (const event of events) window.addEventListener(event, onActivity, { passive: true });
    document.addEventListener("visibilitychange", onActivity, { passive: true });

    const timer = window.setInterval(() => {
      if (isIdleExpired()) {
        signOutLocally(`Cerramos la sesión por ${PORTAL_IDLE_HOURS} horas sin actividad.`);
      }
    }, IDLE_CHECK_MS);

    return () => {
      for (const event of events) window.removeEventListener(event, onActivity);
      document.removeEventListener("visibilitychange", onActivity);
      window.clearInterval(timer);
    };
  }, [status, signOutLocally]);

  const login = useCallback<AuthContextValue["login"]>(
    async (email, password) => {
      setSignedOutReason(null);
      const response = await http.post<
        | { accessToken: string; refreshToken: string; user: AuthUser }
        | { twoFactorRequired: true; challengeId: string }
      >("/api/auth/login", { email, password }, { anonymous: true });

      if ("twoFactorRequired" in response) {
        return { twoFactorRequired: true, challengeId: response.challengeId };
      }

      setSession(response);
      await refreshMe();
      return { ok: true };
    },
    [refreshMe],
  );

  const verifyTwoFactor = useCallback<AuthContextValue["verifyTwoFactor"]>(
    async (challengeId, code) => {
      const response = await http.post<{
        accessToken: string;
        refreshToken: string;
        user: AuthUser;
      }>("/api/auth/2fa", { challengeId, code }, { anonymous: true });
      setSession(response);
      await refreshMe();
    },
    [refreshMe],
  );

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    try {
      if (refreshToken) await http.post("/api/auth/logout", { refreshToken });
    } catch {
      // Si el logout del servidor falla, la sesión local se cierra igual.
    }
    signOutLocally(null);
  }, [signOutLocally]);

  /**
   * RN-ROL-08: elegir sede activa. El endpoint no cambia estado de servidor —la sede viaja
   * en cada header—: lo que hace es validarla y **dejar el rastro** de que la persona
   * cambió de sede.
   */
  const chooseBranch = useCallback(async (branchId: string) => {
    persistActiveBranch(branchId);
    setActiveBranchIdState(branchId);
    await http.post<{ activeBranchId: string }>("/api/me/active-branch", { branchId });
  }, []);

  /**
   * La sede se elige ANTES de prender la vista previa: el cambio de sede es una acción
   * real del Dueño (queda su rastro, RN-ROL-08) y con la vista previa prendida no saldría.
   */
  const startRolePreview = useCallback<AuthContextValue["startRolePreview"]>(
    async (role, branchId) => {
      if (user?.role !== "owner")
        throw new Error("Sólo el Dueño puede ver el sistema como otro rol.");
      const previousBranchId = activeBranchId;
      if (branchId !== activeBranchId) await chooseBranch(branchId);
      const next: RolePreview = { role, branchId, previousBranchId };
      setRolePreview(next);
      setRolePreviewState(next);
    },
    [user, activeBranchId, chooseBranch],
  );

  const stopRolePreview = useCallback(async () => {
    const previous = rolePreview?.previousBranchId ?? null;
    clearRolePreview();
    setRolePreviewState(null);
    if (previous && previous !== activeBranchId && branches.some((b) => b.id === previous)) {
      await chooseBranch(previous);
    }
  }, [rolePreview, activeBranchId, branches, chooseBranch]);

  const value = useMemo<AuthContextValue>(() => {
    // Vista previa: sólo cuenta si la sesión real es del Dueño (ver el efecto de arriba).
    const preview = user?.role === "owner" ? rolePreview : null;
    const shownUser: AuthUser | null =
      user && preview
        ? { ...user, role: preview.role, assignedBranchIds: [preview.branchId] }
        : user;
    const shownBranches = preview
      ? branches.filter((branch) => branch.id === preview.branchId)
      : branches;
    const activeBranch = shownBranches.find((branch) => branch.id === activeBranchId) ?? null;
    return {
      status,
      user: shownUser,
      realRole: user?.role ?? null,
      rolePreview: preview,
      branches: shownBranches,
      activeBranchId,
      activeBranch,
      needsBranchChoice: status === "authenticated" && shownBranches.length > 1 && !activeBranchId,
      signedOutReason,
      login,
      verifyTwoFactor,
      logout,
      chooseBranch,
      refreshMe,
      startRolePreview,
      stopRolePreview,
    };
  }, [
    status,
    user,
    rolePreview,
    branches,
    activeBranchId,
    signedOutReason,
    login,
    verifyTwoFactor,
    logout,
    chooseBranch,
    refreshMe,
    startRolePreview,
    stopRolePreview,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth necesita un <AuthProvider> arriba.");
  return context;
}

/** El usuario ya autenticado. Sólo dentro de las rutas protegidas. */
export function useCurrentUser(): AuthUser {
  const { user } = useAuth();
  if (!user) throw new Error("useCurrentUser fuera de una ruta autenticada.");
  return user;
}
